import { makeAutoObservable } from 'mobx';
import { Benefit } from '@pxtra-client-lib-legacy/types';
import { BenefitSet } from '@pxtra-client-lib-legacy/gen';
import { CardOffer, NO_CARD_OFFER } from '../../../../queries/pxtraCard';
import { CardOfferSaveError, cardOfferSeed, deriveCardOffer, sachbezugLocked } from './cardOffer';

class CreateBenefitSetStore {
  private _name_de = '';
  private _name_en = '';
  private _description_de = '';
  private _description_en = '';
  private _selectedBenefits: Benefit[] = [];
  private _translationAdded = false;
  private _changedFields: string[] = [];
  private _startMonth: number | undefined;
  private _startYear: number | undefined;
  private _forceRetract = false;
  private _forceOverwrite = false;
  private _assignedEmployees: string[] = [];
  private _editingBenefitSetId: string | undefined;
  private _assignmentContext: string | undefined;
  private _cardEnabled = false;
  private _physicalCardEnabled = false;
  private _cardOfferBaseline: CardOffer = NO_CARD_OFFER;
  private _cardOfferLoadedFor: string | undefined;
  private _cardOfferBaselineKnown = false;
  private _cardOfferSavePending = false;
  private _cardOfferSaveError: CardOfferSaveError | undefined;

  get name_de(): string {
    return this._name_de;
  }
  set name_de(value: string) {
    this._name_de = value;
    this._changedFields.push('name_de');
  }

  get name_en(): string {
    return this._name_en;
  }
  set name_en(value: string) {
    this._name_en = value;
    this._changedFields.push('name_en');
  }

  get description_de(): string {
    return this._description_de;
  }
  set description_de(value: string) {
    this._description_de = value;
    this._changedFields.push('description_de');
  }

  get description_en(): string {
    return this._description_en;
  }
  set description_en(value: string) {
    this._description_en = value;
    this._changedFields.push('description_en');
  }

  get selectedBenefits(): Benefit[] {
    return this._selectedBenefits;
  }
  set selectedBenefits(value: Benefit[]) {
    this._selectedBenefits = value;
    this._changedFields.push('selectedBenefits');
  }

  get translationAdded(): boolean {
    return this._translationAdded;
  }
  set translationAdded(value: boolean) {
    this._translationAdded = value;
  }

  get changedFields(): string[] {
    return this._changedFields;
  }

  /** Whether the set itself needs a PATCH; the card offer is saved separately. */
  get hasSetChanges(): boolean {
    return this._changedFields.some((field) => field !== 'cardOffer');
  }

  get cardEnabled(): boolean {
    return this._cardEnabled;
  }
  set cardEnabled(value: boolean) {
    this._cardEnabled = value;
    if (!value) this._physicalCardEnabled = false;
    this._changedFields.push('cardOffer');
  }

  get physicalCardEnabled(): boolean {
    return this._physicalCardEnabled;
  }
  set physicalCardEnabled(value: boolean) {
    this._physicalCardEnabled = value;
    this._changedFields.push('cardOffer');
  }

  get cardOfferBaseline(): CardOffer {
    return this._cardOfferBaseline;
  }

  get cardOfferLoadedFor(): string | undefined {
    return this._cardOfferLoadedFor;
  }

  /** True from the start of an offer save until it succeeds: the set is saved,
   * the offer not yet. */
  get cardOfferSavePending(): boolean {
    return this._cardOfferSavePending;
  }
  set cardOfferSavePending(value: boolean) {
    this._cardOfferSavePending = value;
  }

  /** Why the last offer save failed, until the next one starts. */
  get cardOfferSaveError(): CardOfferSaveError | undefined {
    return this._cardOfferSaveError;
  }
  set cardOfferSaveError(value: CardOfferSaveError | undefined) {
    this._cardOfferSaveError = value;
  }

  get startMonth(): number | undefined {
    return this._startMonth;
  }

  set startMonth(value: number | undefined) {
    this._startMonth = value;
    this._changedFields.push('startMonth');
  }

  get startYear(): number | undefined {
    return this._startYear;
  }

  set startYear(value: number | undefined) {
    this._startYear = value;
  }

  get editingBenefitSetId(): string | undefined {
    return this._editingBenefitSetId;
  }
  set editingBenefitSetId(value: string | undefined) {
    this._editingBenefitSetId = value;
  }

  get assignmentContext(): string | undefined {
    return this._assignmentContext;
  }
  set assignmentContext(value: string | undefined) {
    this._assignmentContext = value;
  }

  set forceRetract(value: boolean) {
    this._forceRetract = value;
  }
  set forceOverwrite(value: boolean) {
    this._forceOverwrite = value;
  }

  set assignedEmployees(value: string[]) {
    this._assignedEmployees = value;
  }

  public clear() {
    this._name_de = '';
    this._name_en = '';
    this._description_de = '';
    this._description_en = '';
    this._selectedBenefits = [];
    this._translationAdded = false;
    this._changedFields = [];
    this._editingBenefitSetId = undefined;
    this._forceRetract = false;
    this._forceOverwrite = false;
    this._startMonth = undefined;
    this._startYear = undefined;
    this._assignedEmployees = [];
    this._assignmentContext = undefined;
    this._cardEnabled = false;
    this._physicalCardEnabled = false;
    this._cardOfferBaseline = NO_CARD_OFFER;
    this._cardOfferLoadedFor = undefined;
    this._cardOfferBaselineKnown = false;
    this._cardOfferSavePending = false;
    this._cardOfferSaveError = undefined;
  }

  public clear_en() {
    this._name_en = '';
    this._description_en = '';
    this._translationAdded = false;
  }

  public buildRequest() {
    return {
      name_de: this._name_de,
      name_en: this._name_en,
      description_de: this._description_de,
      description_en: this._description_en,
      enabled_benefits: this._selectedBenefits,
      ...(this._forceRetract && { force_retract: this._forceRetract })
    };
  }

  public buildAssignmentRequest() {
    return {
      employees: this._assignedEmployees,
      force_retract: this._forceRetract,
      force_overwrite: this._forceOverwrite
    };
  }

  public loadBenefitSet: (benefitSet: BenefitSet | undefined | null) => void = (benefitSet) => {
    if (benefitSet) {
      this._name_de = benefitSet.name_de ?? '';
      this._description_de = benefitSet.description_de ?? '';
      this._selectedBenefits = benefitSet.enabled_benefits ?? [];
      this._editingBenefitSetId = benefitSet.id;
      if (benefitSet.name_en || benefitSet.description_en) {
        this._translationAdded = true;
        this._name_en = benefitSet.name_en ?? '';
        this._description_en = benefitSet.description_en ?? '';
      }
    }
  };

  /** Seeds the card fields and baseline from a fetched offer; see
   * `cardOfferSeed` for when a refetch may still change them. */
  public loadCardOffer(benefitSetId: string, offer: CardOffer, setKnown: boolean) {
    const seed = cardOfferSeed({
      benefitSetId,
      seededFor: this._cardOfferLoadedFor,
      seededKnown: this._cardOfferBaselineKnown,
      setKnown,
      cardFieldsChanged: this._changedFields.includes('cardOffer')
    });
    if (seed === 'skip') return;

    if (seed === 'load') {
      this._cardEnabled = offer.card_enabled;
      this._physicalCardEnabled = offer.physical_card_enabled;
    }
    this._cardOfferBaseline = offer;
    this._cardOfferBaselineKnown = setKnown;
    this._cardOfferLoadedFor = benefitSetId;
  }

  public desiredCardOffer(applies: boolean): CardOffer {
    return deriveCardOffer({
      cardEnabled: this._cardEnabled,
      physicalCardEnabled: this._physicalCardEnabled,
      sachbezugSelected: this._selectedBenefits.includes('sachbezug'),
      applies,
      baseline: this._cardOfferBaseline
    });
  }

  public sachbezugLocked(applies: boolean, editing: boolean): boolean {
    return sachbezugLocked({
      applies,
      editing,
      offerKnown: this._cardOfferLoadedFor === this._editingBenefitSetId,
      baseline: this._cardOfferBaseline,
      selected: this._selectedBenefits.includes('sachbezug')
    });
  }

  public markCardOfferSaved(offer: CardOffer) {
    this._cardOfferBaseline = offer;
    this._cardOfferBaselineKnown = true;
    this._cardOfferSavePending = false;
  }

  constructor() {
    // Plain methods, not actions: observers read them during render and must
    // track what they read.
    makeAutoObservable(this, { desiredCardOffer: false, sachbezugLocked: false });
  }
}

export default CreateBenefitSetStore;
